import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  ApiError,
  del,
  get,
  post,
  type AssetComment,
  type Highlight,
  type Member,
} from "../../lib/api.ts";
import { formatOffset, parseOffset } from "../../lib/timeline.ts";
import { AnnotationMarkers } from "../AnnotationMarkers.tsx";

/* Hallmark · component: disclosure panel (video highlights) · genre: playful · theme: project palette
 * pre-emit critique: P4 H5 E4 S5 R5 V4
 * states: default · hover · focus · active · disabled · loading · error · success
 * contrast: pass (tokens) · mobile: pass at 320/375/414/768 · tokens: pass
 */

/**
 * The marked moments of one video.
 *
 * Only a video has them, so only a video shows this, and it stays folded: a highlight is
 * something an editor goes looking for, not something a traveller opening a clip is asking
 * about. The marks are drawn against the clip's own measured length rather than a player's, so
 * they are there before anything is playing.
 *
 * The clock is the full-screen player, which covers this while it is open — so what the two
 * buttons fill in is where the clip was left when it was last watched. With nothing watched yet
 * they stand down rather than quietly filling in a zero.
 */
export function Highlights({
  tripId,
  assetId,
  durationMs,
  members,
  comments,
  mark,
  onSeek,
  onChanged,
}: {
  tripId: string;
  assetId: string;
  /** The clip's length as the probe measured it; null until it has. */
  durationMs: number | null;
  members: Member[];
  /** The recording's comments, owned by the sheet, for the marks along the clip. */
  comments: AssetComment[];
  /** Where the clip was left when it was last watched, in ms; null if it has not been. */
  mark: number | null;
  /** Open the clip at a moment — the sheet owns the player, so it does the opening. */
  onSeek: (ms: number) => void;
  onChanged: () => void;
}) {
  const base = `/v1/trips/${tripId}/assets/${assetId}`;
  const [items, setItems] = useState<Highlight[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [label, setLabel] = useState("");
  const name = (id: string) => members.find((m) => m.userId === id)?.displayName ?? "A traveller";

  const load = useCallback(async () => {
    const h = await get<{ items: Highlight[] }>(`${base}/highlights`);
    setItems(h.items);
  }, [base]);
  useEffect(() => {
    load().catch((e: Error) => setError(e.message));
  }, [load]);

  async function run(f: () => Promise<unknown>) {
    setError(null);
    setBusy(true);
    try {
      await f();
      await load();
      onChanged();
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 403
          ? "You can't change someone else's entry."
          : (e as Error).message,
      );
    } finally {
      setBusy(false);
    }
  }

  function add(e: FormEvent) {
    e.preventDefault();
    const s = parseOffset(start);
    const en = parseOffset(end);
    if (s === null || en === null) return setError("Use times like 1:24 or 84.");
    if (en <= s) return setError("A highlight must end after it starts.");
    void run(async () => {
      await post(`${base}/highlights`, { startMs: s, endMs: en, ...(label ? { label } : {}) });
      setStart("");
      setEnd("");
      setLabel("");
    });
  }

  /** Fills a field from where the clip was left; unwatched, there is nothing to read. */
  const fill = (set: (v: string) => void) => () => {
    if (mark !== null) set(formatOffset(mark));
  };
  const nowTitle =
    mark !== null ? "Use where the video was left" : "Watch the video to pick a moment";

  return (
    <details className="panel highlights">
      <summary>Highlights{items.length > 0 ? ` (${items.length})` : ""}</summary>
      <div className="stack panelbody">
        <AnnotationMarkers
          durationMs={durationMs}
          comments={comments}
          highlights={items}
          name={name}
          onSeek={onSeek}
        />
        {error && (
          <p role="alert" className="error">
            {error}
          </p>
        )}
        {items.length === 0 && <p className="muted small">No highlights yet.</p>}
        {items.length > 0 && (
          <ul className="plain stack">
            {items.map((h) => (
              <li key={h.id} className="row wrap">
                <strong>
                  {formatOffset(h.startMs)}–{formatOffset(h.endMs)}
                </strong>
                <span className="grow">
                  {h.label ?? ""} <span className="muted">· {name(h.createdBy)}</span>
                </span>
                <button
                  type="button"
                  className="button"
                  disabled={busy}
                  onClick={() => void run(() => del(`/v1/trips/${tripId}/highlights/${h.id}`))}
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
        <form className="row wrap" onSubmit={add}>
          <input
            value={start}
            onChange={(e) => setStart(e.target.value)}
            placeholder="Start 0:12"
            size={8}
            aria-label="Highlight start"
          />
          <button
            type="button"
            className="button"
            onClick={fill(setStart)}
            disabled={mark === null}
            title={nowTitle}
          >
            Mark
          </button>
          <input
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            placeholder="End 0:18"
            size={8}
            aria-label="Highlight end"
          />
          <button
            type="button"
            className="button"
            onClick={fill(setEnd)}
            disabled={mark === null}
            title={nowTitle}
          >
            Mark
          </button>
          <input
            className="grow"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="What happens here? (optional)"
            maxLength={200}
            aria-label="Highlight label"
          />
          <button className="button" disabled={busy}>
            Add highlight
          </button>
        </form>
      </div>
    </details>
  );
}
